import React, { useState, useEffect, useCallback } from "react";
import { CheckCircle } from "react-feather";
import { useTheme } from "../theme/ThemeContext";
import { needsConfirmation } from "../utils/autopay";
import SortSelect from "../components/SortSelect";
import { sortByOption, DEFAULT_SORT } from "../utils/sort";

import { API_BASE, apiFetch } from "../api";

const formatDate = (iso) =>
  new Date(`${iso}T00:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

const daysOverdue = (iso) => {
  const due = new Date(`${iso}T00:00:00`);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((today - due) / (1000 * 60 * 60 * 24));
};

const ActionItems = () => {
  const { tokens, fonts } = useTheme();
  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortBy, setSortBy] = useState(DEFAULT_SORT);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    apiFetch(`${API_BASE}/bills`)
      .then((res) => (res.ok ? res.json() : Promise.reject()))
      .then(setBills)
      .catch(() => setError("Couldn't reach the backend. Make sure it's running with `npm run server`."))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const pending = sortByOption(
    bills.filter((b) => needsConfirmation(b)),
    sortBy,
    { dateKey: "dueDate", amountKey: "amount" }
  );

  const handleConfirmPaid = (bill) => {
    setBills((prev) => prev.map((b) => (b.id === bill.id ? { ...b, status: "Paid" } : b)));
    apiFetch(`${API_BASE}/bills/${bill.id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ status: "Paid" }),
    }).catch(() => load());
  };

  return (
    <div style={{ ...styles.page, backgroundColor: tokens.bg, fontFamily: fonts.body }}>
      <h2 style={{ ...styles.heading, fontFamily: fonts.display, color: tokens.textPrimary }}>Action Items</h2>
      <p style={{ ...styles.subheading, color: tokens.textMuted }}>
        Autopay bills whose pay day has passed but are still marked pending.
      </p>

      <div style={{ ...styles.card, backgroundColor: tokens.surface, border: `1px solid ${tokens.surfaceBorder}`, boxShadow: tokens.cardShadow }}>
        {error && <div style={{ ...styles.banner, backgroundColor: tokens.pendingBg, color: tokens.pending }}>{error}</div>}
        {loading && <p style={{ color: tokens.textMuted, fontSize: 14 }}>Loading...</p>}

        {!loading && !error && pending.length > 0 && (
          <div style={{ display: "flex", justifyContent: "flex-end", padding: "16px 0 0" }}>
            <SortSelect value={sortBy} onChange={setSortBy} />
          </div>
        )}

        {!loading && !error && pending.length === 0 && (
          <div style={styles.emptyState}>
            <CheckCircle size={32} color={tokens.positive} />
            <p style={{ color: tokens.textPrimary, fontWeight: 700, fontSize: 16, margin: "10px 0 4px" }}>
              You're all caught up
            </p>
            <p style={{ color: tokens.textMuted, fontSize: 13.5, margin: 0 }}>
              No autopay bills need confirming right now.
            </p>
          </div>
        )}

        {!loading &&
          pending.map((bill, i) => (
            <div
              key={bill.id}
              style={{
                ...styles.row,
                borderBottom: i === pending.length - 1 ? "none" : `1px solid ${tokens.surfaceBorder}`,
              }}
            >
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: 15, fontWeight: 700, color: tokens.textPrimary }}>{bill.payee}</span>
                  <span
                    style={{
                      fontSize: 10.5,
                      fontWeight: 700,
                      padding: "2px 7px",
                      borderRadius: 999,
                      backgroundColor: tokens.accentSoft,
                      color: tokens.accent,
                    }}
                  >
                    AUTOPAY
                  </span>
                </div>
                <span style={{ fontSize: 12.5, color: tokens.negative, fontWeight: 600 }}>
                  Due {formatDate(bill.dueDate)} &middot; {daysOverdue(bill.dueDate)} day
                  {daysOverdue(bill.dueDate) === 1 ? "" : "s"} overdue
                </span>
                {!!bill.description && (
                  <span style={{ fontSize: 12.5, color: tokens.textMuted }}>{bill.description}</span>
                )}
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                <span style={{ fontFamily: fonts.mono, fontWeight: 700, fontSize: 15, color: tokens.textPrimary }}>
                  ₹{bill.amount.toLocaleString()}
                </span>
                <button
                  onClick={() => handleConfirmPaid(bill)}
                  style={{ ...styles.confirmBtn, backgroundColor: tokens.positive, color: tokens.accentContrast }}
                >
                  <CheckCircle size={14} strokeWidth={2.6} />
                  Confirm Paid
                </button>
              </div>
            </div>
          ))}
      </div>
    </div>
  );
};

const styles = {
  page: {
    padding: "24px",
    minHeight: "100vh",
    transition: "background-color 0.2s ease",
  },
  heading: {
    fontSize: "24px",
    fontWeight: "700",
    margin: 0,
  },
  subheading: {
    fontSize: "14px",
    margin: "6px 0 20px",
  },
  card: {
    padding: "8px 24px",
    borderRadius: "16px",
    transition: "background-color 0.2s ease, border-color 0.2s ease",
  },
  banner: {
    padding: "12px 16px",
    borderRadius: "10px",
    fontSize: "13.5px",
    fontWeight: 600,
    margin: "16px 0",
  },
  emptyState: {
    textAlign: "center",
    padding: "40px 20px",
  },
  row: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "18px 0",
    gap: 16,
    flexWrap: "wrap",
  },
  confirmBtn: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    border: "none",
    borderRadius: 10,
    padding: "9px 14px",
    fontSize: 13,
    fontWeight: 700,
    cursor: "pointer",
  },
};

export default ActionItems;
